import TasksModule from '@modules/TasksModule';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import { InboxContext } from '../types';
import ToastModule from '@modules/ToastModule';
import InboxModule from '..';
import {
  AcceptedOnDevice,
  SkipClassificationReason,
  TaskState,
} from '@modules/TasksModule/types';
import isRingAllQueue from '@lib/utils/isRingAllQueue';
import strings from '../constants/strings';
import { CALL_MISSED_TOAST_DURATION } from '../constants/missedCallToastDuration';
import { inboxState } from '../state';
import BrowserModule from '@modules/BrowserModule';
import { showCallCompletedToast } from '@modules/ToastModule/commonToasts/commonToasts';
import AuthModule from '@modules/AuthModule';
import FullStoryModule from '@modules/FullStoryModule';
import {
  ConversationSessionEndedType,
  ConversationSessionStatus,
} from '@api/generated/graphql';
import { phoneState } from '@modules/SoftPhoneModule/state';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import fullstoryMissedCallTracking from '@modules/InboxModule/lib/fullstory/fullstoryMissedCallTracking';
import DiagnosticsModule, {
  MetricNames,
  MetricTags,
  OperationNames,
  CallDirection,
  CallConnectionErrorTypes,
} from '@modules/DiagnosticsModule';

export type OfferCallWithSessionParams = {
  pcsConversationSid: string;
  from: string;
  displayName: string;
  sourceAddress: string;
  to: string;
};

export type OfferCallWithSoftphoneParams = {
  callId: string;
  remoteIdentity: string;
  localIdentity: string;
};

export function setCallToOfferedWithSession(
  context: InboxContext,
  offerCallParams: OfferCallWithSessionParams,
) {
  const { pcsConversationSid, from, displayName } = offerCallParams;
  context.logger.info('Call Offered With Session Offered', pcsConversationSid);

  // need to create a task with isConnecting set to true
  TasksModule.createTask({
    id: pcsConversationSid,
    from,
    to: offerCallParams.to,
    displayName,
    type: 'voice',
    direction: 'inbound',
    state: TaskState.Offered,
    isStateActive: true,
    attributes: {
      isConnecting: true,
      source: offerCallParams.sourceAddress,
    },
  });

  ToastModule.showIncomingCallToast({
    id: pcsConversationSid,
  });

  InboxModule.evaluateAndSetTaskType(pcsConversationSid);

  // browser notification
  if (document.visibilityState === 'hidden') {
    BrowserModule.showBrowserNotification('ServiceTitan', {
      body: 'You have an incoming call.',
    });
  }
  InboxModule.setSelectedTaskId(pcsConversationSid);
}

export function setCallToOfferedWithSoftphoneCall(
  context: InboxContext,
  offerCallParams: OfferCallWithSoftphoneParams,
) {
  const { callId, remoteIdentity, localIdentity } = offerCallParams;
  context.logger.info('Call Offered With Softphone callStarted', callId);

  // extract `+128123123123` from `128123123123 (+128123123123)` format
  const remoteIdentityMatch = remoteIdentity.match(/\((\+\d+)\)/);
  const extractedRemoteIdentity = remoteIdentityMatch
    ? remoteIdentityMatch[1]
    : remoteIdentity;

  TasksModule.createTask({
    id: callId,
    from: extractedRemoteIdentity,
    displayName: extractedRemoteIdentity,
    type: 'voice',
    direction: 'inbound',
    state: TaskState.Offered,
    attributes: { callId, remoteIdentity, localIdentity }, // so that we can find the task once we get the session event
    isStateActive: true,
  });

  ToastModule.showIncomingCallToast({
    id: callId,
  });

  InboxModule.evaluateAndSetTaskType(callId);

  // browser notification
  if (document.visibilityState === 'hidden') {
    BrowserModule.showBrowserNotification('ServiceTitan', {
      body: 'You have an incoming call.',
    });
  }
  InboxModule.setSelectedTaskId(callId);
}

export function setCallToAccepted(
  context: InboxContext,
  taskId: string,
  kazooCallId?: string,
  acceptedOn?: AcceptedOnDevice,
) {
  context.logger.info('call Accepted', taskId, { acceptedOn });

  const task = TasksModule.getTask(taskId);

  if (!task) {
    return;
  }

  ToastModule.hideIncomingCallToast(taskId, task.attributes.callId);

  const isDid = InboxModule.isDid(task);

  if (isDid) {
    InboxModule.resetMissedCallsDid();
  } else {
    InboxModule.resetMissedCalls();
  }

  TasksModule.setTaskAttribute(task.id, 'isAnswering', false);

  // Set the acceptedOn attribute if provided
  if (acceptedOn) {
    TasksModule.setTaskAttribute(task.id, 'acceptedOn', acceptedOn);
  }

  TasksModule.setTaskState(task.id, TaskState.Accepted);
  InboxModule.fetchRecordingStatus(kazooCallId, task.attributes.callId);

  // re-query for recording settings if call might be from a transfer
  const sessionSid = task.attributes.sessionSid;
  if (!sessionSid) {
    console.warn('No sessionSid found in task attributes:', task.attributes);
    return;
  }
  SoftPhoneModule.getConversationBySid(sessionSid)
    .then(({ data, error }) => {
      if (error) {
        console.error('Error fetching conversation by SID:', error);
        return;
      }
      if (!data?.conversationBySid?.sessions) {
        console.warn('No conversation found for sessionSid:', sessionSid);
        return;
      }
      for (const session of data.conversationBySid.sessions) {
        if (
          session.value.status == ConversationSessionStatus.Ended &&
          session.value.endedInfo?.type == ConversationSessionEndedType.Transfer
        ) {
          InboxModule.fetchRecordingStatus(
            kazooCallId,
            task.attributes.callId,
            true,
          );
          break;
        }
      }
    })
    .catch((error) => {
      console.error('Failed to fetch conversation:', error);
    });
}

export function setCallToRejected({
  context,
  taskId,
  eventType,
}: {
  context: InboxContext;
  taskId: string;
  eventType?: string;
}) {
  context.logger.info('call Rejected', taskId);

  const task = TasksModule.getTask(taskId);
  if (task) {
    const { queueId, queueName } = task;
    if (queueId && queueName && !isRingAllQueue(queueId)) {
      // if it's a ring all queue, we don't want to show the toast
      // or handle the missed call count - because it could still be answered by another agent
      InboxModule.handleMissedCallsForQueue(queueId);
      const toastText = strings.labels.toasts.missedCallForQueue(
        inboxState.missedCallsForQueue[queueId],
        queueName,
      );
      ToastModule.showToast({
        status: 'info',
        duration: CALL_MISSED_TOAST_DURATION,
        title: toastText,
      });
      fullstoryMissedCallTracking({
        task,
        sessionSid: taskId,
        source: 'rejected',
        eventType,
      });
    }
    ToastModule.hideIncomingCallToast(taskId, task.attributes.callId);
    TasksModule.setTaskState(task.id, TaskState.Rejected);
    TasksModule.setTaskAttribute(task.id, 'recordInteraction', false);
    TasksModule.setTaskAttribute(task.id, 'isAnswering', false);
    TasksModule.deleteTask(task.id);
  }
}

/**
 * Handles the case where an external call is abandoned via either
 * - caller hanging up before the call is accepted
 * - call is hit the queue rollover time limit and timed out
 *
 * This function checks if the call timed out from the queue and shows
 * an appropriate toast message based on whether it was a timeout or
 * a caller hangup.
 *
 * @param context InboxContext
 * @param sessionSid string
 */
export function setCallToAbandoned({
  context,
  sessionSid,
  eventType,
}: {
  context: InboxContext;
  sessionSid: string;
  eventType?: string;
}) {
  context.logger.info('call Abandoned', sessionSid);

  const task = TasksModule.getTask(sessionSid);

  if (!task) {
    return;
  }

  // Check if the task has timed out from the queue
  const queueTimeoutAt = task.attributes.queueTimeoutAt;
  const hasTimedOut = queueTimeoutAt && Date.now() >= queueTimeoutAt;

  const isDid = InboxModule.isDid(task);

  if (
    hasTimedOut &&
    FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureQueueRolloverTimeoutNotificationEnabled,
    )
  ) {
    // Show queue timeout toast
    ToastModule.hideIncomingCallToast(sessionSid, task.attributes.callId);
    ToastModule.showToast({
      id: `queue-timeout-${sessionSid}`,
      title: 'Call Timed Out',
      toastBody: strings.labels.toasts.rolloverTimeout,
      status: 'info',
      duration: 8000,
    });
  } else if (isDid) {
    // Show DID abandoned call toast
    InboxModule.handleMissedDidCall();

    const toastTitle = strings.labels.toasts.callMissedDidTitle(
      inboxState.missedCallsDid,
    );

    ToastModule.hideIncomingCallToast(sessionSid, task.attributes.callId);
    ToastModule.showToast({
      title: toastTitle,
      status: 'info',
      duration: CALL_MISSED_TOAST_DURATION,
    });
  } else {
    // Show Queue abandoned call toast
    const toastTitle = strings.labels.toasts.callMissedExternalTitle;
    ToastModule.hideIncomingCallToast(sessionSid, task.attributes.callId);
    ToastModule.showToast({
      title: toastTitle,
      status: 'info',
      duration: CALL_MISSED_TOAST_DURATION,
    });
  }

  fullstoryMissedCallTracking({
    task,
    sessionSid,
    source: 'abandoned',
    eventType,
    queueTimeoutOccurred: hasTimedOut,
  });

  TasksModule.setTaskLoading(task.id, false);
  TasksModule.setTaskState(task.id, TaskState.Abandoned);
  TasksModule.setTaskAttribute(task.id, 'recordInteraction', false);
  TasksModule.setTaskAttribute(task.id, 'shouldClassify', false);
  TasksModule.setTaskAttribute(task.id, 'isConnecting', false);
  TasksModule.setTaskAttribute(task.id, 'isAnswering', false);
}

export function setCallWrapupTime(
  context: InboxContext,
  sessionSid: string,
  timestamp: string | number,
  wrapupAllowedMs: number,
) {
  context.logger.info('setting call wrap up time', sessionSid);

  const task = TasksModule.getTask(sessionSid);
  if (task) {
    const wrapUpStartedAt = new Date(timestamp).getTime();
    task.recoveryStartedAt = wrapUpStartedAt;
    task.recoveryEndsAt = wrapUpStartedAt + wrapupAllowedMs;
  }
}

const completeCallWithSuccessToast = (
  taskId: string,
  skipReason?: SkipClassificationReason,
) => {
  TasksModule.setTaskComplete(taskId);
  showCallCompletedToast(skipReason);
};

export async function setCallToCompleted(
  context: InboxContext,
  taskId: string,
  classified: boolean,
) {
  context.logger.info('call Completed', taskId);
  const task = TasksModule.getTask(taskId);
  if (!task) {
    return;
  }

  if (task.state !== TaskState.WrapUp) {
    TasksModule.setTaskComplete(taskId);
    return;
  }

  if (!task.attributes.shouldClassify) {
    completeCallWithSuccessToast(taskId);
  }

  // classification was completed, close the task
  if (classified) {
    completeCallWithSuccessToast(taskId);
    return;
  }

  if (!AuthModule.hasAccessToTenant(task.tenantId)) {
    // agent doesn't have access to the tenant, it could be due to misconfiguration or transferred call
    TasksModule.setTaskAttribute(task.id, 'recordInteraction', false);
    completeCallWithSuccessToast(
      taskId,
      SkipClassificationReason.NoTenantAccess,
    );
    return;
  }

  const classificationRequirement =
    await TasksModule.checkClassificationRequirement(task.id);

  if (classificationRequirement === null) {
    // error case, do nothing
    return;
  }

  if (classificationRequirement === true) {
    context.parent.showClassificationModalForTaskId(taskId);
  } else if (
    classificationRequirement === SkipClassificationReason.NotRequired
  ) {
    // no classification required, close the task and send request
    completeCallWithSuccessToast(taskId, classificationRequirement);
  } else if (classificationRequirement === 'NotReady') {
    // classification is still processing, show warning toast
    ToastModule.showToast({
      title: 'Please Wait',
      status: 'warning',
      toastBody:
        'Call classification is still processing, please wait and try again in a few minutes.',
    });
  } else if (classificationRequirement === 'NotEnded') {
    // call has not ended yet, show warning toast
    ToastModule.showToast({
      title: 'Please Wait',
      status: 'warning',
      toastBody:
        'Call cannot be closed until it has ended, please wait and try again in a few minutes.',
    });
  } else {
    // classification is handled by another agent, close the task without sending request
    TasksModule.setTaskAttribute(task.id, 'recordInteraction', false);
    completeCallWithSuccessToast(taskId, classificationRequirement);
  }
}

export function setCallToCompletedByOthers(taskId: string) {
  const task = TasksModule.getTask(taskId);
  if (!task) {
    return;
  }
  TasksModule.setTaskAttribute(task.id, 'recordInteraction', false);
  completeCallWithSuccessToast(
    taskId,
    SkipClassificationReason.ClassificationCompleted,
  );
}

export function setCallToTimedOut({
  context,
  sessionSid,
  eventType,
}: {
  context: InboxContext;
  sessionSid: string;
  eventType?: string;
}) {
  context.logger.info('call Timed Out', sessionSid);

  const task = TasksModule.getTask(sessionSid);

  if (!task) {
    return;
  }

  // only if it's not a sim ring (ring all) queue call as time outs are
  // handled by setExternalCallToAbandoned event for sim ring queues (due to receiving same events for both cases)
  const { queueId, queueName } = task;
  if (task && !isRingAllQueue(queueId ?? '')) {
    ToastModule.hideIncomingCallToast(sessionSid, task.attributes.callId);
    // priority is handling the case where
    if (task.attributes?.isConnecting) {
      // the task got timed out before we receive the softphone callStarted event so we need to show a different toast
      if (phoneState.phoneConnected) {
        DiagnosticsModule.incrementCounter(MetricNames.ERROR_TOTAL, {
          [MetricTags.OPERATION_NAME]: OperationNames.CALL_CONNECTION,
          [MetricTags.DIRECTION]: CallDirection.Inbound.toLowerCase(),
          [MetricTags.ERROR_TYPE]: CallConnectionErrorTypes.TIMEOUT,
        });
        // we only want to track this event if the phone is connected in the current tab.
        FullStoryModule.trackEvent('[CCP-Call] Connecting Cancelled', {
          sessionSid,
        });
      }
      ToastModule.showToast({
        status: 'info',
        duration: CALL_MISSED_TOAST_DURATION,
        title: strings.labels.toasts.errorConnection,
      });
    } else if (queueId && queueName) {
      InboxModule.handleMissedCallsForQueue(queueId);
      const toastText = strings.labels.toasts.missedCallForQueue(
        inboxState.missedCallsForQueue[queueId],
        queueName,
      );
      ToastModule.showToast({
        status: 'info',
        duration: CALL_MISSED_TOAST_DURATION,
        title: toastText,
      });
      fullstoryMissedCallTracking({
        task,
        sessionSid,
        source: 'call-to-timeout',
        eventType,
      });
    }
    TasksModule.setTaskAttribute(task.id, 'recordInteraction', false);
    TasksModule.setTaskAttribute(task.id, 'isAnswering', false);
    TasksModule.setTaskState(task.id, TaskState.TimedOut);
  }
}

export function setCallToWrapup(context: InboxContext, sessionSid: string) {
  context.logger.info('setting call to WrapUp', sessionSid);

  const task = TasksModule.getTask(sessionSid);
  if (task) {
    TasksModule.setTaskState(task.id, TaskState.WrapUp);
  }
}
